import {
  push,
  set,
  update,
  remove,
  onValue,
  off,
  get,
  notificationsRef,
  notificationRef,
} from '@/firebase/database';
import type { Notification, NotificationCategory, NotificationType, Permission } from '@/types';

export interface UserAuthContextSubset {
  uid?: string;
  isAdmin: boolean;
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
}

/**
 * Evaluates whether a user with a given role/permissions can view a specific notification.
 * - Admins can view ALL notifications.
 * - Staff/Users ONLY view notifications relevant to their operational billing/customer/inventory permissions.
 * - Security & Staff Login alerts are strictly hidden from staff and only delivered to Admin.
 */
export const canUserSeeNotification = (
  notification: Notification,
  user: UserAuthContextSubset,
): boolean => {
  if (!user) return false;

  // Admin sees all notifications
  if (user.isAdmin) return true;

  // 🚫 Security & Staff Login alerts are EXCLUSIVELY for Admin
  const isLoginAlert =
    notification.category === 'security' ||
    notification.type === 'staff_login' ||
    notification.title?.toLowerCase().includes('login') ||
    notification.message?.toLowerCase().includes('logged in') ||
    notification.message?.toLowerCase().includes('signed in');

  if (isLoginAlert) {
    return false;
  }

  // 🚫 Staff & Team management notifications (Admin only)
  if (notification.category === 'staff') {
    return user.canAny(['User.Manage', 'Role.Manage']);
  }

  // 🚫 Settings notifications (Admin only)
  if (notification.category === 'settings') {
    return user.can('Settings.Manage');
  }

  // Category-based operational permissions for staff
  switch (notification.category) {
    case 'invoice':
      return user.canAny(['Invoice.View', 'Invoice.Create', 'Invoice.Edit', 'Invoice.Finalize']);
    case 'payment':
      return user.canAny(['Payment.View', 'Payment.Create']);
    case 'customer':
      return user.canAny(['Customer.View', 'Customer.Create', 'Customer.Edit']);
    case 'inventory':
      return user.canAny(['Product.View', 'Product.Create', 'Product.Edit']);
    case 'system':
      return true;
    default:
      return false;
  }
};

/**
 * Returns the list of categories a user is allowed to view in tabs.
 */
export const getPermittedCategoriesForUser = (
  user: UserAuthContextSubset,
): (NotificationCategory | 'all')[] => {
  if (!user) return ['all'];
  if (user.isAdmin) {
    return ['all', 'invoice', 'payment', 'customer', 'inventory', 'staff', 'security', 'settings'];
  }

  const list: (NotificationCategory | 'all')[] = ['all'];

  if (user.canAny(['Invoice.View', 'Invoice.Create', 'Invoice.Edit', 'Invoice.Finalize'])) {
    list.push('invoice');
  }
  if (user.canAny(['Payment.View', 'Payment.Create'])) {
    list.push('payment');
  }
  if (user.canAny(['Customer.View', 'Customer.Create', 'Customer.Edit'])) {
    list.push('customer');
  }
  if (user.canAny(['Product.View', 'Product.Create', 'Product.Edit'])) {
    list.push('inventory');
  }
  if (user.canAny(['User.Manage', 'Role.Manage'])) {
    list.push('staff');
  }
  if (user.can('AuditLog.View')) {
    list.push('security');
  }
  if (user.can('Settings.Manage')) {
    list.push('settings');
  }

  return list;
};

export interface CreateNotificationInput {
  companyId: string;
  category: NotificationCategory;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  userId?: string;
  userName?: string;
  createdAt?: number;
}

/**
 * Creates a real-time notification in the company's notification stream.
 */
export const createNotification = async (input: CreateNotificationInput): Promise<string | null> => {
  if (!input.companyId) return null;
  try {
    const listRef = notificationsRef(input.companyId);
    const newRef = push(listRef);
    const id = newRef.key as string;

    const notifData: Notification = {
      id,
      companyId: input.companyId,
      category: input.category,
      type: input.type,
      title: input.title,
      message: input.message,
      read: false,
      link: input.link || '',
      userId: input.userId || '',
      userName: input.userName || '',
      createdAt: typeof input.createdAt === 'number' ? input.createdAt : Date.now(),
    };

    await set(newRef, notifData);
    return id;
  } catch (err) {
    console.error('Failed to create notification:', err);
    return null;
  }
};

/**
 * Subscribes to real-time notifications for a company, ordered newest first.
 */
export const subscribeToNotifications = (
  companyId: string,
  callback: (notifications: Notification[]) => void,
): (() => void) => {
  if (!companyId) {
    callback([]);
    return () => {};
  }

  const listRef = notificationsRef(companyId);
  const unsubscribe = onValue(
    listRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback([]);
        return;
      }

      const list: Notification[] = [];
      snapshot.forEach((child) => {
        const val = child.val();
        if (val && typeof val === 'object') {
          list.push({
            id: child.key as string,
            companyId: val.companyId || companyId,
            category: val.category || 'system',
            type: val.type || 'system',
            title: val.title || 'Notification',
            message: val.message || '',
            read: !!val.read,
            link: val.link || '',
            userId: val.userId || '',
            userName: val.userName || '',
            createdAt: typeof val.createdAt === 'number' ? val.createdAt : Date.now(),
          });
        }
      });

      // Sort newest first
      list.sort((a, b) => b.createdAt - a.createdAt);
      callback(list);
    },
    (err) => {
      console.warn('Notifications subscription error:', err);
      callback([]);
    },
  );

  return () => off(listRef, 'value', unsubscribe);
};

/**
 * Mark a single notification as read.
 */
export const markNotificationAsRead = async (companyId: string, notificationId: string): Promise<void> => {
  if (!companyId || !notificationId) return;
  try {
    await update(notificationRef(companyId, notificationId), { read: true });
  } catch (err) {
    console.error('Failed to mark notification as read:', err);
  }
};

/**
 * Mark all notifications as read for a company.
 */
export const markAllNotificationsAsRead = async (
  companyId: string,
  notifications: Notification[],
): Promise<void> => {
  if (!companyId || !notifications.length) return;
  try {
    const updates: Record<string, boolean> = {};
    notifications.forEach((n) => {
      if (!n.read) {
        updates[`${n.id}/read`] = true;
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(notificationsRef(companyId), updates);
    }
  } catch (err) {
    console.error('Failed to mark all notifications as read:', err);
  }
};

/**
 * Delete a specific notification.
 */
export const deleteNotification = async (companyId: string, notificationId: string): Promise<void> => {
  if (!companyId || !notificationId) return;
  try {
    await remove(notificationRef(companyId, notificationId));
  } catch (err) {
    console.error('Failed to delete notification:', err);
  }
};

/**
 * Ensures notifications are populated with recent actual company activity if empty.
 */
export const ensureRecentActivityNotifications = async (companyId: string): Promise<void> => {
  if (!companyId) return;
  try {
    const snap = await get(notificationsRef(companyId));
    if (snap.exists() && Object.keys(snap.val() || {}).length > 0) {
      return; // Already has notifications
    }

    // Fetch recent invoices
    const invSnap = await get(notificationsRef(companyId).parent ? (await import('@/firebase/database')).invoicesRef(companyId) : null as any);
    if (invSnap && invSnap.exists()) {
      let count = 0;
      invSnap.forEach((child: any) => {
        if (count >= 5) return;
        const inv = child.val();
        if (inv) {
          createNotification({
            companyId,
            category: 'invoice',
            type: inv.status === 'finalized' ? 'invoice_finalized' : 'invoice_created',
            title: inv.status === 'finalized' ? 'Estimate Finalized' : 'Estimate Generated',
            message: `${inv.invoiceNumber || 'Estimate'} for ${inv.customerName || 'Customer'} (₹${(inv.grandTotal || 0).toLocaleString('en-IN')})`,
            link: `/invoices/${child.key}`,
            createdAt: typeof inv.createdAt === 'number' ? inv.createdAt : Date.now() - count * 1000 * 60 * 30,
          });
          count++;
        }
      });
    }

    // Fetch recent payments
    const paySnap = await get((await import('@/firebase/database')).paymentsRef(companyId));
    if (paySnap && paySnap.exists()) {
      let count = 0;
      paySnap.forEach((child: any) => {
        if (count >= 3) return;
        const pay = child.val();
        if (pay) {
          createNotification({
            companyId,
            category: 'payment',
            type: 'payment_received',
            title: 'Payment Received',
            message: `₹${(pay.amount || 0).toLocaleString('en-IN')} received for ${pay.invoiceNumber || 'Estimate'}`,
            link: `/payments`,
            createdAt: typeof pay.createdAt === 'number' ? pay.createdAt : Date.now() - count * 1000 * 60 * 45,
          });
          count++;
        }
      });
    }

    // Fetch recent customers
    const custSnap = await get((await import('@/firebase/database')).customersRef(companyId));
    if (custSnap && custSnap.exists()) {
      let count = 0;
      custSnap.forEach((child: any) => {
        if (count >= 3) return;
        const cust = child.val();
        if (cust) {
          createNotification({
            companyId,
            category: 'customer',
            type: 'customer_created',
            title: 'Customer Added',
            message: `${cust.name || 'New Customer'} (${cust.customerCode || 'Client'}) registered`,
            link: `/customers`,
            createdAt: typeof cust.createdAt === 'number' ? cust.createdAt : Date.now() - count * 1000 * 60 * 60,
          });
          count++;
        }
      });
    }
  } catch (err) {
    console.warn('ensureRecentActivityNotifications error:', err);
  }
};

/**
 * Clear all notifications for a company.
 */
export const clearAllNotifications = async (companyId: string): Promise<void> => {
  if (!companyId) return;
  try {
    await remove(notificationsRef(companyId));
  } catch (err) {
    console.error('Failed to clear notifications:', err);
  }
};
